import React from 'react';
import {
  Box, Paper, Stack, Typography, Chip, Button, IconButton, Divider, Tooltip,
  CircularProgress, Grid,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import VerifiedIcon from '@mui/icons-material/Verified';
import HandshakeIcon from '@mui/icons-material/Handshake';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';

const ICP_LABEL = {
  CurrentFit: 'Current Fit',
  FuturePotential: 'Future Potential',
  NotAFit: 'Not a Fit',
  Unknown: 'Unknown',
};
const ICP_COLOR = {
  CurrentFit: 'success',
  FuturePotential: 'info',
  NotAFit: 'error',
  Unknown: 'default',
};

const SIGNAL_STATUS_COLOR = {
  Open: 'default',
  Triggered: 'success',
  Dismissed: 'warning',
};

const SEGMENT_TYPE_COLOR = {
  Approach:    { bg: '#ede9fe', text: '#5b21b6' },
  Industry:    { bg: '#dbeafe', text: '#1e40af' },
  Vertical:    { bg: '#dcfce7', text: '#166534' },
  UseCase:     { bg: '#fef3c7', text: '#92400e' },
  GeoMarket:   { bg: '#fce7f3', text: '#9d174d' },
  CompanySize: { bg: '#e0e7ff', text: '#3730a3' },
};

export default function IcpProfilePanel({
  lead,
  icpData,
  icpLoading,
  onRegenerate,
  enriching,
  onRefresh,
  onUpdateSignalStatus,
  onDeleteSignal,
  onRemoveSegment,
}) {
  const verdict = lead?.aiIcpFit;
  const partner = lead?.aiPartnerCandidate === true;
  const partnerType = lead?.aiPartnerType;
  const partnerReason = lead?.aiPartnerReason;
  const signals = icpData?.signals || [];
  const segments = icpData?.segments || [];

  // Group segments by Type
  const segmentsByType = segments.reduce((acc, m) => {
    const k = m.segmentationType || 'Other';
    (acc[k] = acc[k] || []).push(m);
    return acc;
  }, {});

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <VerifiedIcon sx={{ color: '#6366f1' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>ICP Profile</Typography>
          {verdict && (
            <Chip
              size="small"
              label={`ICP: ${ICP_LABEL[verdict] || verdict}`}
              color={ICP_COLOR[verdict] || 'default'}
              variant="filled"
            />
          )}
          {partner && (
            <Tooltip title={partnerReason || 'Channel-partner candidate'}>
              <Chip
                size="small"
                icon={<HandshakeIcon sx={{ fontSize: 16 }} />}
                label={partnerType ? `Partner: ${partnerType}` : 'Channel Partner'}
                sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }}
              />
            </Tooltip>
          )}
          {lead?.status && (
            <Chip size="small" variant="outlined" label={`Status: ${lead.status}`} />
          )}
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={onRefresh} disabled={icpLoading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small" variant="outlined"
            startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
            onClick={onRegenerate} disabled={enriching}
            sx={{ textTransform: 'none' }}>
            Re-enrich
          </Button>
        </Stack>
      </Paper>

      {/* ICP Fit summary card */}
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary' }}>
          ICP FIT — extracted from enrichment
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Verdict</Typography>
            <Typography sx={{ fontWeight: 600 }}>
              {verdict ? (ICP_LABEL[verdict] || verdict) : '—'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Partner Candidate</Typography>
            <Typography sx={{ fontWeight: 600 }}>
              {lead?.aiPartnerCandidate === true ? 'Yes' :
               lead?.aiPartnerCandidate === false ? 'No' : '—'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Partner Type</Typography>
            <Typography sx={{ fontWeight: 600 }}>{partnerType || '—'}</Typography>
          </Grid>
          {partner && partnerReason && (
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" color="text.secondary">Partner reason</Typography>
              <Typography>{partnerReason}</Typography>
            </Grid>
          )}
        </Grid>

        {!verdict && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No ICP fit verdict extracted yet.
            </Typography>
            <Button size="small" variant="contained" startIcon={<AutoAwesomeIcon />}
              onClick={onRegenerate} disabled={enriching}>
              Run enrichment
            </Button>
          </Box>
        )}
      </Paper>

      {/* Watch Signals */}
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <VisibilityIcon sx={{ color: '#6366f1', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Signals to Watch ({signals.length})
          </Typography>
        </Stack>
        {icpLoading && signals.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : signals.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No watch signals yet — re-enrich to extract them from the AI brief.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {signals.map((s) => (
              <Paper key={s.id} variant="outlined" sx={{
                p: 1.25, display: 'flex', alignItems: 'flex-start', gap: 1,
                bgcolor: s.status === 'Triggered' ? '#f0fdf4' :
                         s.status === 'Dismissed' ? '#fafafa' : '#fff',
              }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', mb: 0.5 }}>
                    <Chip size="small" label={s.status} color={SIGNAL_STATUS_COLOR[s.status] || 'default'} />
                    {s.category && <Chip size="small" variant="outlined" label={s.category} />}
                    <Chip size="small" variant="outlined" label={s.source}
                      sx={{ color: s.source === 'Ai' ? '#6366f1' : 'inherit' }} />
                  </Stack>
                  <Typography variant="body2">{s.text}</Typography>
                  {s.evidence && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Evidence: {s.evidence}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={0.5}>
                  {s.status !== 'Triggered' && (
                    <Tooltip title="Mark triggered">
                      <IconButton size="small" onClick={() => onUpdateSignalStatus(s.id, 'Triggered')}>
                        <CheckCircleIcon fontSize="small" sx={{ color: '#16a34a' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {s.status !== 'Dismissed' && (
                    <Tooltip title="Dismiss">
                      <IconButton size="small" onClick={() => onUpdateSignalStatus(s.id, 'Dismissed')}>
                        <CancelIcon fontSize="small" sx={{ color: '#dc2626' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => onDeleteSignal(s.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Segments */}
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <CategoryIcon sx={{ color: '#6366f1', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Segments ({segments.length})
          </Typography>
        </Stack>
        {icpLoading && segments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : segments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No segments assigned yet.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {Object.entries(segmentsByType).map(([type, members]) => {
              const palette = SEGMENT_TYPE_COLOR[type] || { bg: '#f3f4f6', text: '#374151' };
              return (
                <Box key={type}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary',
                    letterSpacing: 0.5, textTransform: 'uppercase' }}>
                    {type}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                    {members.map((m) => (
                      <Tooltip key={m.id} title={m.reason || `${m.assignSource} assigned`}>
                        <Chip
                          size="small"
                          label={m.segmentationName}
                          onDelete={() => onRemoveSegment(m.id)}
                          sx={{
                            bgcolor: palette.bg,
                            color: palette.text,
                            fontWeight: 500,
                            '& .MuiChip-deleteIcon': { color: palette.text, opacity: 0.6 },
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
